import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import { Switch } from "@headlessui/react";
import { Alert } from "../Alert";
import { Button } from "../Button";
import {
  clearConsultant,
  clearUpdateConsultant,
  requestGetConsultant,
  requestUpdateConsultant,
  useAuthState,
  useConsultantDispatch,
  useConsultantState,
} from "../../Context";
import { classNames, getErrorMessages } from "../../Utils";

function ToggleRow({ id, label, checked, onChange, disabled }) {
  return (
    <div className="flex items-center justify-between py-3">
      <label
        htmlFor={id}
        className="text-sm font-medium text-gray-700 select-none"
      >
        {label}
      </label>
      <Switch
        id={id}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className={classNames(
          checked ? "bg-blue-600" : "bg-gray-200",
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
          "relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
        )}
      >
        <span className="sr-only">{label}</span>
        <span
          className={classNames(
            checked ? "translate-x-5" : "translate-x-0",
            "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200",
          )}
        />
      </Switch>
    </div>
  );
}

function ToggleRowSkeleton() {
  return (
    <div className="flex items-center justify-between py-3 animate-pulse">
      <div className="h-5 w-40 bg-gray-300 rounded-md" />
      <div className="h-6 w-11 bg-gray-300 rounded-full" />
    </div>
  );
}

function StatTile({ label, children, className = "" }) {
  return (
    <div className={`border border-gray-200 rounded-md p-3 ${className}`}>
      <p className="text-xs text-slate-500">{label}</p>
      <div className="mt-1 text-sm font-semibold text-blue-600">{children}</div>
    </div>
  );
}

function StatTileSkeleton() {
  return (
    <div className="border border-gray-200 rounded-md p-3 animate-pulse">
      <div className="h-3 w-20 bg-gray-300 rounded-md" />
      <div className="mt-2 h-4 w-24 bg-gray-300 rounded-md" />
    </div>
  );
}

/**
 * Compact consultant availability settings shown on the Dashboard in place of
 * the consultation form. Basic toggles only; the full form lives in Settings.
 */
export function ConsultantSettingsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user } = useAuthState();
  const dispatch = useConsultantDispatch();
  const {
    consultant,
    loading,
    submitLoading,
    submitError,
    updateConsultantStatus,
  } = useConsultantState();

  const [active, setactive] = useState(false);
  const [acceptCall, setacceptCall] = useState(false);
  const [acceptZoom, setacceptZoom] = useState(false);

  useEffect(() => {
    requestGetConsultant(dispatch, { userID: user.id });
    return () => {
      clearConsultant(dispatch);
      clearUpdateConsultant(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!consultant) return;
    setactive(!!consultant.active);
    // Inactive consultants receive nothing — render call/zoom as off too.
    setacceptCall(!!consultant.active && !!consultant.acceptCall);
    setacceptZoom(!!consultant.active && !!consultant.acceptZoom);
  }, [consultant]);

  const handleActiveChange = (value) => {
    setactive(value);
    if (!value) {
      setacceptCall(false);
      setacceptZoom(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!consultant) return;

    // The update endpoint overwrites the whole record, so send the current
    // schedule back or it would be reset to backend defaults. addServices /
    // deleteServices stay omitted so the service links are left untouched.
    requestUpdateConsultant(dispatch, {
      active,
      acceptCall,
      acceptZoom,
      activeDays:
        consultant.activeDays?.length === 7 ? consultant.activeDays : undefined,
      startTimes:
        consultant.startTimes?.length === 7 ? consultant.startTimes : undefined,
      endTimes:
        consultant.endTimes?.length === 7 ? consultant.endTimes : undefined,
      workStartYear: consultant.workStartYear || undefined,
    });
  };

  return (
    <form action="#" onSubmit={handleSubmit}>
      <div className="flex flex-col p-4 gap-2">
        {updateConsultantStatus === "SUCCESS" ? (
          <Alert
            type="success"
            message={t("Consultant settings updated successfully")}
          />
        ) : null}
        {submitError ? (
          <Alert
            type="error"
            message={
              getErrorMessages(submitError)[0] ||
              t("Error updating consultant settings.")
            }
            errors={getErrorMessages(submitError)}
          />
        ) : null}

        {loading || !consultant ? (
          <div className="grid grid-cols-2 gap-2">
            <StatTileSkeleton />
            <StatTileSkeleton />
            <StatTileSkeleton />
            <StatTileSkeleton />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <StatTile label={t("Main Balance")}>
              <NumericFormat
                displayType={"text"}
                value={consultant.balance || 0}
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix={"Rp"}
              />
            </StatTile>
            <StatTile label={t("Pending Balance")}>
              <NumericFormat
                displayType={"text"}
                value={consultant.pendingBalance || 0}
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix={"Rp"}
              />
            </StatTile>
            <StatTile label={t("Rating")}>
              {consultant.rating || 0}
              <span className="ml-1 text-xs font-medium text-gray-500">
                {t("from")} {consultant.lastRating || 0}
              </span>
            </StatTile>
            <div className="flex items-stretch">
              <Button
                className="w-full"
                color="secondary"
                title={t("Withdraw Balance")}
                onClick={() => navigate("/consultant-withdraw")}
              />
            </div>
          </div>
        )}

        <div className="divide-y divide-gray-200">
          {loading || !consultant ? (
            <>
              <ToggleRowSkeleton />
              <ToggleRowSkeleton />
              <ToggleRowSkeleton />
            </>
          ) : (
            <>
              <ToggleRow
                id="widget-active"
                label={t("Accept Consultation")}
                checked={active}
                onChange={handleActiveChange}
                disabled={submitLoading}
              />
              <ToggleRow
                id="widget-accept-call"
                label={t("Receive Video Audio Call")}
                checked={acceptCall}
                onChange={setacceptCall}
                disabled={submitLoading || !active}
              />
              <ToggleRow
                id="widget-accept-zoom"
                label={t("Receive Zoom Call")}
                checked={acceptZoom}
                onChange={setacceptZoom}
                disabled={submitLoading || !active}
              />
            </>
          )}
        </div>
      </div>

      <div className="p-4 flex justify-between items-center bg-gray-50 rounded-b-lg">
        <a
          href="/settings/consultant"
          className="text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          {t("manageFullSettings")}
        </a>
        <Button
          type="submit"
          title={t("Save")}
          disabled={loading || submitLoading || !consultant}
          loading={submitLoading}
        />
      </div>
    </form>
  );
}

export default ConsultantSettingsWidget;
